import { Cast, Film } from '../services';
import { Event, Movie, Organization, Person, WebSite } from 'schema-dts';

import { CinemaModel } from '../services/cinemas';
import getConfig from 'next/config';
import { jsonLdScriptProps } from 'react-schemaorg';
import { toHoursAndMinutes } from './format-minutes-to-hours';

/* Getting the public runtime configuration from the next.config.js file. */
const { publicRuntimeConfig } = getConfig();

enum Types {
  Movie = 'Movie',
  Person = 'Person',
  ScreeningEvent = 'ScreeningEvent',
}

/**
 * It takes an array of `Cast` objects and returns an array of `Person` objects
 * @param {Cast[]} casts - Cast[]
 * @returns An array of objects with the type Person.
 */
const getActors = (casts: Cast[]): Person[] => {
  return casts.map(cast => {
    return {
      '@type': Types.Person,
      'name': `${cast.firstName} ${cast.lastName}`,
    };
  });
};

/**
 * It returns a React component that renders two JSON-LD scripts.
 * @returns A React Fragment with two script tags.
 */
export const buildOrganizationSchemaComponent = () => {
  return (
    <>
      <script
        {...jsonLdScriptProps<Organization>({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          'name': publicRuntimeConfig.siteName,
          'alternateName': 'muvi',
          url: publicRuntimeConfig.nextPublicBaseUrl,
          logo: `${publicRuntimeConfig.nextPublicBaseUrl}icons/logos/muvi-logo.svg`,
          'sameAs': [
            'https://www.instagram.com/muvi_cinemas/',
            'https://twitter.com/muvicinemas',
            'https://www.facebook.com/muvicinemas/',
            'https://www.snapchat.com/add/muvicinemas',
          ],
        })}
      />
      <script
        {...jsonLdScriptProps<WebSite>({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          'name': publicRuntimeConfig.siteName,
          'alternateName': 'muvi',
          'url': publicRuntimeConfig.nextPublicBaseUrl,
        })}
      />
    </>
  );
};

/**
 * It takes a movie and a list of cinemas and returns a React component that renders a JSON-LD script
 * tag with the movie and cinema data.
 * @param {Film} movie - Film - this is the movie object that contains all the movie details
 * @param {CinemaModel[]} cinemas - CinemaModel[]
 * @returns A React component.
 */
export const buildEventSchemaComponent = (movie: Film, cinemas: CinemaModel[]) => {
  return (
    <>
      <script
        {...jsonLdScriptProps<Movie>({
          '@context': 'https://schema.org',
          '@type': Types.Movie,
          'name': movie.title,
          'description': movie.descriptionTag,
          ...(movie.casts?.length > 0 && { 'actor': getActors(movie.casts) }),
          ...(movie.directors?.length > 0 && {
            'director': getActors([movie.directors[0]]),
          }),
          'image': movie.image,
          'duration': `T${toHoursAndMinutes(movie.runtime)}`,
          'genre': movie.genres?.map(genre => genre.name),
          'subtitleLanguage': movie.subtitle,
          'inLanguage': movie.movieLang,
        })}
      />
      <script
        {...jsonLdScriptProps<Event>({
          '@context': 'https://schema.org',
          '@type': Types.ScreeningEvent,
          'description': movie.descriptionTag,
          'location': cinemas.map(cinema => ({
            '@type': 'MovieTheater',
            'name': `${cinema.name}`,
          })),
          'workPresented': {
            '@type': Types.Movie,
            'name': movie.title,
            'sameAs': movie.imdb,
          },
          'inLanguage': movie.movieLang,
          'videoFormat': 'Digital',
        })}
      />
    </>
  );
};
